import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import {
  GeneralFilter,
  Location,
  Partner,
  Project,
  Service,
  ServiceType,
} from '@app/shared';
import { NzDrawerService } from 'ng-zorro-antd/drawer';
import { ActivatedRoute, Router } from '@angular/router';
import { CreateServiceDrawerComponent } from '../../drawers/create-service-drawer/create-service-drawer.component';
import { ServiceDrawerComponent } from '../../drawers/service-drawer/service-drawer.component';
import {
  ScreenService,
  StServiceService,
  ProjectService,
  setGetParamsById,
  FlowEventService,
} from '@app/core';
import { combineLatest, Subject, take, takeUntil } from 'rxjs';
import { GlobalValuesService } from '../../../../core/services/global-values.service';
import { AuthService } from '../../../../core/services/auth.service';
import { ProjectUtilsService } from '../../services/project-utils.service';
import {
  ConfirmationModalComponent,
  ConfirmationModalConfig,
} from '../../../../shared/components/confirmation-modal/confirmation-modal.component';

@Component({
  selector: 'app-project-tabset',
  standalone: false,

  templateUrl: './project-tabset.component.html',
  styleUrl: './project-tabset.component.less',
})
export class ProjectTabsetComponent
  implements AfterViewInit, OnInit, OnDestroy
{
  @Input() project!: Project;
  @Input() withPadding: boolean = true;
  @Input() activeTabIndex: number = 0;
  @Input() mobileSearchClass: string = 'mobile-search-filter';
  @Output() navigateEvent = new EventEmitter<void>();
  @Output() isDocDrawerOpen: EventEmitter<boolean> = new EventEmitter<boolean>(
    false
  );

  // Obtener los templates de cada tab
  @ViewChild('inboxTmpl', { static: true }) inboxTmpl!: TemplateRef<any>;
  @ViewChild('serviceTmpl', { static: true }) serviceTmpl!: TemplateRef<any>;
  @ViewChild('docTmpl', { static: true }) docTmpl!: TemplateRef<any>;
  @ViewChild('teamTmpl', { static: true }) teamTmpl!: TemplateRef<any>;
  @ViewChild('containersTmpl', { static: true })
  containersTmpl!: TemplateRef<any>;
  @ViewChild('confirmationModal')
  confirmationModal!: ConfirmationModalComponent;

  afterViewInit: boolean = false;

  tabs: { name: string; icon: string; tmpl: TemplateRef<any> }[] = [];

  selectedService: Service | undefined;

  createServiceDrawer: any;
  serviceTypeFiltervalue: number = 0;
  servicesFilters: GeneralFilter[] = [
    {
      name: 'Tipo de servicio',
      icon: 'arrow_drop_down',
      active: false,
    },
  ];

  statusFilters: GeneralFilter[] = [
    { name: 'Todos', icon: 'all_inclusive', active: true, code: 'All' },
    {
      name: 'No validado',
      icon: 'close',
      active: false,
      code: 'NonValidated',
      class: 'subtle-red',
    },
    {
      name: 'Validado',
      icon: 'verified',
      active: false,
      code: 'Validated',
      class: 'subtle-purple',
    },
    {
      name: 'Planificación',
      icon: 'event_upcoming',
      active: false,
      code: 'Planning',
      class: 'subtle-pink',
    },
    {
      name: 'En ejecución',
      icon: 'motion_play',
      active: false,
      code: 'InExecution',
      class: 'subtle-blue',
    },
    {
      name: 'Finalizado',
      icon: 'check_circle',
      active: false,
      code: 'Finalized',
      class: 'subtle-teal',
    },
  ];
  statusFilterValue: string = 'All';

  providerFilterValue: number = 0;
  providersFilters: GeneralFilter[] = [
    {
      name: 'Proveedor',
      icon: 'arrow_drop_down', // o el ícono que prefieras
      active: false,
    },
  ];

  selectedServicesIds = new Set<number>();

  showServiceFilters: boolean = true;

  isMobile$;

  showServices: Service[] = [];
  allServiceTypes: ServiceType[] = [];
  servOnlyOnProject: ServiceType[] = [];
  allProviderList: Partner[] = [];
  providersOnlyOnProject: Partner[] = [];
  locations: Location[] = [];
  defaultFlowEvents: any[] = []; // Event types por defecto para cuando no hay flow asignado

  destroy$ = new Subject<void>();
  pendingDeleteServiceId: number | undefined;

  constructor(
    private cdRef: ChangeDetectorRef,
    private drawerService: NzDrawerService,
    private stService: StServiceService,
    public authService: AuthService,
    public ps: ProjectUtilsService,
    private screenService: ScreenService,
    private route: ActivatedRoute,
    private router: Router,
    public gs: GlobalValuesService,
    private projectService: ProjectService,
    private flowEventService: FlowEventService
  ) {
    this.isMobile$ = screenService.isMobile$;
  }

  // Saber si todos estan seleccionados
  get isAllSelected(): boolean {
    return this.project.services!.every((item) =>
      this.selectedServicesIds.has(item.id)
    );
  }

  // Saber si solo algunos estan seleccionados
  get isIndeterminate(): boolean {
    const hasSelected = this.project.services!.some((item) =>
      this.selectedServicesIds.has(item.id)
    );
    return !this.isAllSelected && hasSelected;
  }

  ngOnInit() {
    // Cargar event types por defecto para servicios sin flow asignado
    this.loadDefaultFlowEvents();

    this.filterServices();

    this.gs.serviceTypes$.pipe(takeUntil(this.destroy$)).subscribe({
      next: (serviceTypes) => {
        this.allServiceTypes = serviceTypes || [];
        this.servOnlyOnProject = this.getUniqueServiceTypesForProject();
      },
      error: (err) => {
        console.error('Error al cargar serviceTypes', err);
        this.allServiceTypes = [];
      },
    });

    this.gs.isPartnerReady$
      .pipe(takeUntil(this.destroy$))
      .subscribe((value) => {
        if (value) {
          this.allProviderList = this.gs.providerList;
          this.providersOnlyOnProject = this.getUniqueProvidersForProject();
        }
      });

    this.gs.places$
      .pipe(takeUntil(this.destroy$))
      .subscribe((value) => (this.locations = value));

    // Suscribirse a cambios cuando se crean órdenes
    this.ps.ordersCreated$
      .pipe(takeUntil(this.destroy$))
      .subscribe((ordersCreated) => {
        if (ordersCreated) {
          console.log('DEBUG: Órdenes creadas - recargando datos de las cards');
          this.refreshData();
        }
      });
  }

  onFilterSelect(id: number) {
    if (this.serviceTypeFiltervalue === id) {
      this.serviceTypeFiltervalue = 0;
    } else {
      this.serviceTypeFiltervalue = id;
    }
    this.servicesFilters[0].active = !!this.serviceTypeFiltervalue;
    this.filterServices();
  }

  onProviderFilterSelect(id: number | null) {
    if (id === null) {
      // Limpiar filtro
      this.providerFilterValue = 0;
    } else if (this.providerFilterValue === id) {
      // Deseleccionar si es el mismo
      this.providerFilterValue = 0;
    } else {
      // Seleccionar nuevo
      this.providerFilterValue = id;
    }

    this.providersFilters[0].active = !!this.providerFilterValue;
    this.filterServices();
  }

  filterServices() {
    let tempFilter = this.project.services;

    if (tempFilter && tempFilter.length > 0 && this.serviceTypeFiltervalue) {
      tempFilter = tempFilter!.filter(
        (s) => s.typeId === this.serviceTypeFiltervalue
      );
    }
    if (tempFilter && tempFilter.length > 0 && this.providerFilterValue) {
      tempFilter = tempFilter.filter(
        (s) => s.supplierId === this.providerFilterValue
      );
    }

    // Filtrar por status
    if (
      tempFilter &&
      tempFilter.length > 0 &&
      this.statusFilterValue &&
      this.statusFilterValue !== 'All'
    ) {
      tempFilter = tempFilter.filter((service) => {
        const serviceState = this.getServiceState(service);
        return serviceState === this.statusFilterValue;
      });
    }

    // Crear copias profundas para forzar detección de cambios en propiedades anidadas
    this.showServices = (tempFilter || []).map((service: Service) => ({
      ...service,
      serviceOrders: service.serviceOrders ? [...service.serviceOrders] : [],
    }));

    if (
      !this.authService.loggedUser$.value?.viewUnassignedProjectsAndServices
    ) {
      this.showServices = this.showServices.filter(
        (s) => s.supplierId === this.authService.loggedUser$.value?.partner.id
      );
    }

    console.log(
      'DEBUG: showServices updated:',
      this.showServices.length,
      'services with orders:',
      this.showServices.map((s) => ({
        id: s.id,
        orders: s.serviceOrders?.length || 0,
      }))
    );
  }

  getServiceState(service: Service): string | undefined {
    let state: string | undefined;

    // Si el servicio tiene un campo state, usarlo
    if ((service as any).state) {
      state = (service as any).state;
    }
    // Si no, usar el estado del primer serviceOrder si existe
    else if (service.serviceOrders && service.serviceOrders.length > 0) {
      state = service.serviceOrders[0].state?.toString();
    }
    // Si no hay serviceOrders, usar estado por defecto basado en el primer evento del flow
    else if (service.flow?.events && service.flow.events.length > 0) {
      state = service.flow.events[0].id.toString(); // Usar el ID del primer evento del flow
    }
    // Si no hay flow, usar el primer evento de los eventos por defecto del sistema
    else if (this.defaultFlowEvents.length > 0) {
      state = this.defaultFlowEvents[0].id.toString();
    }
    // Último fallback
    else {
      state = '1'; // Usar '1' como último recurso
    }

    // Debug solo para servicios sin estado válido
    if (!state || state === '1') {
      console.log('DEBUG: getServiceState for service', service.id, {
        finalState: state,
        hasServiceOrders: !!service.serviceOrders,
        serviceOrdersCount: service.serviceOrders?.length,
        hasFlow: !!service.flow,
        flowEventsCount: service.flow?.events?.length,
        firstFlowEvent: service.flow?.events?.[0],
      });
    }

    return state;
  }

  private loadDefaultFlowEvents() {
    // Cargar event types disponibles en el sistema para usar como fallback
    this.flowEventService
      .getEventType({
        startRow: 0,
        endRow: 100, // Obtener los primeros 100 event types
        sortProperty: 'sequence',
        sortDirection: 'asc',
      })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response: any) => {
          this.defaultFlowEvents = response.data?.rows || [];
          console.log(
            'DEBUG: Default flow events loaded:',
            this.defaultFlowEvents.length
          );
        },
        error: (error) => {
          console.error('Error loading default flow events:', error);
          // Crear un evento por defecto básico
          this.defaultFlowEvents = [
            {
              id: 1,
              name: 'Tarea creada',
              icon: 'check_circle',
              color: '#FF990A',
              sequence: 1,
            },
          ];
        },
      });
  }

  getServiceStateString(service: Service): string | undefined {
    const state = this.getServiceState(service);
    return state ?? undefined;
  }

  getCompletedOrdersCount(service: Service): number {
    if (!service.serviceOrders || service.serviceOrders.length === 0) {
      return 0;
    }
    // Contar órdenes completadas (asumiendo que 'completed' o similar es el estado completado)
    return service.serviceOrders.filter(
      (order) =>
        order.state === 'completed' ||
        order.state === 'done' ||
        order.state === 'finished'
    ).length;
  }

  onStatusFilterSelect(filterCode: string): void {
    this.statusFilterValue = filterCode;
    this.statusFilters.forEach((f) => {
      f.active = f.code === filterCode;
    });
    this.filterServices();
  }

  ngAfterViewInit(): void {
    this.tabs = [
      // {name: 'Inbox', icon: 'inbox', tmpl: this.inboxTmpl},
      { name: 'Tareas', icon: 'checklist', tmpl: this.serviceTmpl },
      { name: 'Documentos', icon: 'description', tmpl: this.docTmpl },
      // {
      //   name: 'Contenedores',
      //   icon: 'calendar_view_week',
      //   tmpl: this.containersTmpl,
      // },
      // {name: 'Equipos', icon: 'warehouse', tmpl: this.teamTmpl},
    ];

    this.afterViewInit = true;
    this.cdRef.detectChanges();
  }

  emitNavigateEvent(serviceId: number): void {
    this.navigateEvent.emit();
    // Navegar usando Router para evitar problemas en producción
    this.router
      .navigate(['/projects', 'detail', this.project.id, 'service', serviceId])
      .catch((error) => {
        console.error('Error navigating to service:', error);
      });
  }

  openCreateServiceDrawer(): void {
    combineLatest({
      isMobile: this.screenService.isMobile$,
      isMedium: this.screenService.isMedium$,
    })
      .pipe(take(1))
      .subscribe(({ isMobile, isMedium }) => {
        this.createServiceDrawer = this.drawerService.create({
          nzWidth: 793,
          nzHeight: '98%',
          nzPlacement: isMobile ? 'bottom' : 'right',
          nzClosable: false,
          nzMaskClosable: false,
          nzContent: CreateServiceDrawerComponent,
          nzData: {
            project: this.project,
            serviceId:
              this.route.snapshot.params['serviceId'] ||
              this.ps.selectedService?.id, // Obtener serviceId de URL o servicio seleccionado
          },
        });

        this.createServiceDrawer.afterClose
          .pipe(takeUntil(this.destroy$), take(1))
          .subscribe(() => {
            this.filterServices();
            this.cdRef.detectChanges();
          });
      });
  }

  // Alternamiento individual
  toggleSelectService(id: number): void {
    if (this.selectedServicesIds.has(id)) {
      this.selectedServicesIds.delete(id);
    } else {
      this.selectedServicesIds.add(id);
    }
  }

  // Alternamiento total
  toggleSelectAllServices(): void {
    const newSet = new Set(this.selectedServicesIds);

    if (this.isAllSelected) {
      this.project.services?.forEach((item) => newSet.delete(item.id));
    } else {
      this.project.services?.forEach((item) => newSet.add(item.id));
    }

    this.selectedServicesIds = newSet;
  }

  sendIsDocDrawerOpen(value: boolean) {
    this.isDocDrawerOpen.emit(value);
  }

  toggleShowFilters() {
    this.showServiceFilters = !this.showServiceFilters;
  }

  getUniqueServiceTypesForProject(): ServiceType[] {
    //Verificar si el proyecto tiene servicios
    if (!this.project?.services?.length) {
      return [];
    }
    //  Obtener ids únicos
    const uniqueTypeIds = new Set<number>(
      this.project.services
        .map((service) => service.typeId)
        .filter((typeId): typeId is number => typeof typeId === 'number')
    );
    //Filtrar la lista completa de tipos
    return this.allServiceTypes.filter((serviceType) =>
      uniqueTypeIds.has(serviceType.id)
    );
  }

  getUniqueProvidersForProject(): any[] {
    if (!this.project?.services?.length) {
      return [];
    }

    // Obtener IDs de proveedores únicos usados en los servicios del proyecto
    const uniqueProviderIds = new Set<number>(
      this.project.services
        .map((service) => service.supplierId)
        .filter(
          (providerId): providerId is number => typeof providerId === 'number'
        )
    );

    const result = this.gs.providerList.filter((provider) =>
      uniqueProviderIds.has(provider.id)
    );

    return result;
  }

  closeAllDrawers() {
    this.ps.closeAllDrawers$.next(true);
    setTimeout(() => {
      this.ps.closeAllDrawers$.next(false);
    }, 100);
  }
  refreshData() {
    console.log('DEBUG: refreshData called - reloading project data');

    // Recargar el proyecto completo para obtener servicios con serviceOrders incluidas
    if (this.project) {
      this.projectService
        .getProject(setGetParamsById(this.project.id))
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (response: any) => {
            console.log(
              'DEBUG: Project reloaded from server - Full response:',
              response
            );
            console.log('DEBUG: response.data exists:', !!response.data);
            console.log(
              'DEBUG: response.data.services exists:',
              !!response.data?.services
            );

            // La respuesta tiene estructura {data: {rows: [project]}}
            const updatedProject = response.data.rows[0];

            console.log('DEBUG: updatedProject exists:', !!updatedProject);
            console.log(
              'DEBUG: updatedProject.services length:',
              updatedProject?.services?.length
            );

            // Solo actualizar los servicios para mantener otras propiedades del proyecto
            if (this.project && updatedProject?.services) {
              console.log(
                'DEBUG: Updating services with new data from rows[0]'
              );
              // Crear copias profundas de los servicios para forzar detección de cambios en propiedades anidadas
              this.project.services = updatedProject.services.map(
                (service: Service) => ({
                  ...service,
                  serviceOrders: service.serviceOrders
                    ? [...service.serviceOrders]
                    : [],
                })
              );

              // Crear una nueva instancia del proyecto para forzar detección de cambios
              this.ps.selectedProject = {
                ...this.project,
                services: this.project.services,
              };
              // Forzar una nueva asignación del proyecto completo para que Angular detecte cambios
              this.project = { ...this.project };
            } else {
              console.log(
                'DEBUG: No services found in updatedProject, keeping existing data'
              );
            }

            // Volver a aplicar filtros con los nuevos datos
            this.filterServices();
            console.log(
              'DEBUG: showServices after refresh:',
              this.showServices.length,
              'services'
            );

            // Recargar tipos de servicio y proveedores
            this.servOnlyOnProject = this.getUniqueServiceTypesForProject();
            this.providersOnlyOnProject = this.getUniqueProvidersForProject();

            // Forzar detección de cambios y re-renderización
            this.cdRef.markForCheck();
            this.cdRef.detectChanges();

            // Pequeño delay para asegurar que la UI se actualice
            setTimeout(() => {
              this.cdRef.markForCheck();
              this.cdRef.detectChanges();
            }, 100);

            console.log(
              'ProjectTabsetComponent: Proyecto recargado para actualizar servicios'
            );
          },
          error: (error) => {
            console.error('Error recargando proyecto:', error);
            // Fallback: solo filtrar datos existentes
            this.filterServices();
            this.servOnlyOnProject = this.getUniqueServiceTypesForProject();
            this.providersOnlyOnProject = this.getUniqueProvidersForProject();
            this.cdRef.detectChanges();
          },
        });
    } else {
      // Fallback si no hay proyecto
      this.filterServices();
      this.servOnlyOnProject = this.getUniqueServiceTypesForProject();
      this.providersOnlyOnProject = this.getUniqueProvidersForProject();
      this.cdRef.detectChanges();

      console.log('ProjectTabsetComponent: Vista actualizada (sin proyecto)');
    }
  }

  getLocation(id: number | undefined): Location | undefined {
    return this.locations.find((l) => l.id === id);
  }

  getAssignedPartner(service: Service): Partner | undefined {
    // Primero intentar usar el supplier del servicio si está disponible
    if (service.supplier) {
      return service.supplier;
    }

    // Si no, buscar por supplierId
    if (service.supplierId) {
      const partner = this.getPartnerById(service.supplierId);
      if (partner) return partner;
    }

    // Si no hay supplier, usar el partner del proyecto
    if (this.project.partner) {
      return this.project.partner;
    }

    return undefined;
  }

  getPartnerById(id: number | undefined): Partner | undefined {
    if (!id) return undefined;
    return this.gs.providerList.find((p) => p.id === id);
  }

  openBulkDeleteConfirmation(): void {
    const config: ConfirmationModalConfig = {
      title: 'Eliminar servicios',
      message: `¿Estás seguro que deseas eliminar ${this.selectedServicesIds.size} servicio(s)?`,
      confirmText: 'Eliminar',
      cancelText: 'Cancelar',
      confirmButtonType: 'primary',
      confirmButtonDanger: true,
    };
    this.confirmationModal.config = config;
    this.confirmationModal.open();
  }

  onServiceMenuAction(action: string, service: Service): void {
    switch (action) {
      case 'edit':
        // Abrir drawer de edición
        this.openEditServiceDrawer(service);
        break;
      case 'delete':
        // Eliminar servicio individual
        this.openDeleteServiceConfirmation(service);
        break;
    }
  }

  openEditServiceDrawer(service: Service): void {
    // Configurar el servicio seleccionado para que funcione la tabla de subtareas
    this.ps.selectedService = service;

    combineLatest({
      isMobile: this.screenService.isMobile$,
      isMedium: this.screenService.isMedium$,
    })
      .pipe(take(1))
      .subscribe(({ isMobile, isMedium }) => {
        const drawerRef = this.drawerService.create({
          nzWidth: 793,
          nzHeight: '98%',
          nzPlacement: isMobile ? 'bottom' : 'right',
          nzClosable: false,
          nzMaskClosable: false,
          nzContent: ServiceDrawerComponent,
          nzData: {
            project: this.project,
            service: service,
          },
        });

        drawerRef.afterClose
          .pipe(takeUntil(this.destroy$), take(1))
          .subscribe(() => {
            this.filterServices();
            this.cdRef.detectChanges();
          });
      });
  }

  openDeleteServiceConfirmation(service: Service): void {
    this.pendingDeleteServiceId = service.id;
    const config: ConfirmationModalConfig = {
      title: 'Eliminar servicio',
      message: `¿Estás seguro que deseas eliminar el servicio "${
        service.name || 'Sin nombre'
      }"?`,
      confirmText: 'Eliminar',
      cancelText: 'Cancelar',
      confirmButtonType: 'primary',
      confirmButtonDanger: true,
    };
    this.confirmationModal.config = config;
    this.confirmationModal.open();
  }

  onDeleteConfirmed(data: { textValue?: string; dateValue?: Date }): void {
    if (this.pendingDeleteServiceId) {
      this.deleteService(this.pendingDeleteServiceId);
      this.pendingDeleteServiceId = undefined;
    } else if (this.selectedServicesIds.size > 0) {
      this.deleteSelectedServices();
    }
  }

  deleteService(serviceId: number): void {
    this.stService
      .deleteService([serviceId])
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          // Refrescar datos
          this.filterServices();
          this.cdRef.detectChanges();
        },
        error: (error: any) => {
          console.error('Error eliminando servicio:', error);
          // Resetear el estado de procesamiento del modal
          this.confirmationModal.config.processing = false;
          this.confirmationModal.forceClose();
        },
      });
  }

  deleteSelectedServices(): void {
    const serviceIds = Array.from(this.selectedServicesIds);
    this.stService
      .deleteService(serviceIds)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          // Limpiar selección
          this.selectedServicesIds.clear();
          // Refrescar datos
          this.filterServices();
          this.cdRef.detectChanges();
        },
        error: (error: any) => {
          console.error('Error eliminando servicios:', error);
          // Resetear el estado de procesamiento del modal
          this.confirmationModal.config.processing = false;
          this.confirmationModal.forceClose();
        },
      });
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
